<template>
  <b-container id="the-list">
    <article class="list-container">
      <loading-mask v-bind:is-loading="isLoading">
        <section class="header">
          <div class="left">
            <!-- タイトル -->
            <h1 class="title" v-t="'theList.user'" />
            <!-- 件数 -->
            <p class="count">
              {{ $t('theList.count', { n: count }) }}
            </p>
            <!-- ソート解除リンク -->
            <a
              v-if="hasSort"
              class="link clear-sort"
              v-on:click="clearSort"
              v-t="'theList.action.clearSort'"
            />
          </div>
          <div class="right">
            <!-- 更新日時 -->
            <span class="label" v-show="listUpdatedLabel">
              {{ $t('theList.listUpdate.field') }} :
              {{ listUpdatedLabel }}
            </span>
            <!-- 更新ボタン -->
            <button-reload v-on:click="updateListData" />
            <!-- 検索ボタン -->
            <b-button
              variant="light"
              size="sm"
              class="button search"
              v-bind:class="{ 'has-condition': !isAllSearchStateEmpty }"
              v-on:click="togglePanel('filter')"
            >
              <icon-search class="icon" />
              <span class="text" v-t="'theList.action.search'" />
              <span class="text hidden" v-t="'theList.action.search'" />
            </b-button>
            <!-- CSV一括ダウンロードボタン -->
            <b-button
              variant="light"
              size="sm"
              class="button download"
              v-on:click="downloadCsv(false)"
            >
              <icon-download class="icon" />
              <span v-t="'theList.action.downloadAllAsCsv'" />
            </b-button>
            <!-- 表示項目ボタン -->
            <b-button
              variant="light"
              size="sm"
              class="button select"
              v-on:click="togglePanel('column')"
            >
              <icon-setting class="icon" />
              <span v-t="'theList.action.selectFields'" />
            </b-button>
          </div>
        </section>
        <div class="contents">
          <div class="grid-container">
            <data-grid
              v-bind:header-height="ROW_HEIGHT"
              v-bind:row-height="ROW_HEIGHT"
              v-bind:data-type-definitions="dataTypeDefinitions"
              v-bind:default-col-def="defaultColumnDef"
              v-bind:column-defs="columnDefsWithInitialState"
              v-bind:row-data="data"
              v-bind:context="context"
              v-bind:is-external-filter-present="isFilterPresent"
              v-bind:does-external-filter-pass="filterFunction"
              v-model="value"
              return-object
              class="table"
              v-on:grid-ready="onGridReady"
              v-on:first-data-rendered="onFirstDataRendered"
              v-on:column-any-changed="onColumnChanged"
              v-on:sort-changed="onColumnChanged"
              v-on:model-updated="onRowModelUpdated"
            />
            <section v-if="valueCount" class="footer">
              <!-- 件数 -->
              <p class="count">
                {{ $tc('theList.selected', valueCount) }}
              </p>
              <div class="action">
                <!-- CSVダウンロード -->
                <b-button
                  variant="light"
                  class="button download"
                  v-on:click="downloadCsv(true)"
                >
                  <icon-download class="icon" />
                  <span v-t="'theList.action.downloadAsCsv'" />
                </b-button>
                <!-- メッセージ -->
                <b-button
                  v-if="isMessageAvailable"
                  class="button message"
                  size="sm"
                  variant="light"
                  v-ifield-b-tooltip.hover
                  v-bind:title="$t('theList.action.createMessage')"
                  v-on:click.stop="$_showMessagePost"
                >
                  <icon-message class="icon" />
                </b-button>
              </div>
              <!-- 解除リンク -->
              <a
                class="link deselect"
                v-t="'theList.action.deselect'"
                v-on:click="$_deselect"
              />
            </section>
          </div>
          <user-list-panel-filter
            v-bind:condition="condition"
            v-model="isPanelShow.filter"
            v-on:change:condition="updateCondition"
            v-on:change:isAllEmpty="isAllSearchStateEmpty = $event"
          />
          <user-list-panel-column-visible
            v-bind:column-defs="columnDefs"
            v-bind:visible="columnVisible"
            v-model="isPanelShow.column"
            v-on:change:visible="applyColumnVisible"
          />
        </div>
      </loading-mask>
    </article>
    <message-post-dialog
      ref="message-post-dialog"
      v-bind:default-receive-users="defaultReceiveUsers"
    />
  </b-container>
</template>

<script>
import ButtonReload from '@/components/AppButtonReload.vue'
import IconSearch from '@/assets/ifield/ic_search.svg?component'
import IconDownload from '@/assets/ifield/ic_download.svg?component'
import IconSetting from '@/assets/ifield/ic_view-settings.svg?component'
import IconMessage from '@/assets/ifield/ic_message.svg?component'
import DataGrid from '@/components/AppDataGrid.vue'
import UserListPanelFilter from '@/components/user/AppUserListPanelFilter.vue'
import UserListPanelColumnVisible from '@/components/user/AppUserListPanelColumnVisible.vue'
import MessagePostDialog from '@/components/message/AppMessagePostDialog.vue'
import moment from 'moment'
import i18n from '@/i18n'
import { FUNCTION_TYPE } from '@/constants'
import { ref, computed } from 'vue'
import { useHandleApiError } from '@/composables/UseHandleApiError'
import { useUserListColumnDefinition } from '@/composables/user/UseUserListColumnDefinition'
import { useUserListColumnState } from '@/composables/user/UseUserListColumnState'
import { useUserListFilterCondition } from '@/composables/user/UseUserListFilterCondition'
import { useUserListFilterFunction } from '@/composables/user/UseUserListFilterFunction'
import { useUserListCsv } from '@/composables/user/UseUserListCsv'
import { useUserListData } from '@/composables/user/UseUserListData'

/**
 * 行の高さ(px)
 * ヘッダーにも同じ値を適用する
 */
const ROW_HEIGHT = 38

export default {
  name: 'TheList',
  components: {
    ButtonReload,
    IconSearch,
    IconDownload,
    IconSetting,
    IconMessage,
    DataGrid,
    UserListPanelFilter,
    UserListPanelColumnVisible,
    MessagePostDialog,
  },
  setup() {
    const { handleApiError } = useHandleApiError()

    /**
     * ---
     * ag-grid API の利用
     * ---
     */
    const gridApi = ref(null)
    /**
     * gridReady イベントコールバック
     * @param {GridReadyEvent} event
     */
    const onGridReady = (event) => {
      gridApi.value = event.api
    }

    /**
     * ローディングフラグ
     * @type {Ref.<boolean>}
     */
    const isLoading = ref(false)

    /**
     * ---
     * ユーザーリスト用データの利用
     * ---
     */
    const { data, updateDate, fetchData } = useUserListData()
    /**
     * リストデータを更新する(エラーハンドリング込)
     */
    const updateListData = async () => {
      isLoading.value = true

      try {
        await fetchData()
      } catch (error) {
        handleApiError(error)
      } finally {
        isLoading.value = false
      }
    }
    /**
     * データ件数
     * @type {Ref.<number>}
     */
    const count = ref(data.value.length)
    /**
     * RowModel更新時処理(データ,ソート,フィルタ更新時の処理)
     * * データ件数更新
     */
    const onRowModelUpdated = () => {
      let newCount = 0
      gridApi.value?.forEachNode((node) => {
        newCount += +node.displayed
      })
      count.value = newCount
    }
    /**
     * 表示用更新時刻
     * @type {ComputedRef.<string>}
     */
    const listUpdatedLabel = computed(() => {
      if (!updateDate.value) return ''
      return moment(updateDate.value).format(
        i18n.t('theList.listUpdate.format')
      )
    })

    /**
     * ---
     * カラム定義の利用
     * ---
     */
    const {
      dataTypeDefinitions,
      defaultColumnDef,
      columnDefs,
      context: columnDefsContext,
      autoWidthColumnsOnFirstRendered,
    } = useUserListColumnDefinition({
      updateDate,
    })
    const {
      columnDefsWithInitialState,
      columnStates,
      updateColumnStates,
      hasSort,
      clearSort,
      columnVisible,
      applyColumnVisible,
    } = useUserListColumnState({ gridApi, columnDefs })
    /**
     * カラムに関する各種状態変更イベントのコールバック
     */
    const onColumnChanged = () => {
      const columnState = gridApi.value?.getColumnState()
      columnState && updateColumnStates(columnState)
    }
    /**
     * 初回データレンダリング完了イベントのコールバック
     * * カラム幅のfit(対象カラムのみ)
     */
    const onFirstDataRendered = () => {
      gridApi.value?.autoSizeColumns(autoWidthColumnsOnFirstRendered)
    }

    /**
     * グリッド表示に使用するコンテキストデータ
     * @type {ComputedRef.<Object>}
     */
    const context = computed(() => ({
      ...columnDefsContext.value,
    }))

    /**
     * ---
     * パネル表示の利用
     * ---
     */
    /**
     * パネル表示状態
     * @type {Ref.<Object>}
     */
    const isPanelShow = ref({
      column: false,
      filter: false,
    })
    /**
     * パネル表示を切り替える
     * @param {string} panel パネル種類
     */
    const togglePanel = (panel) => {
      // 両方とも true の状態を避けるため初期値falseをベースに敷く
      isPanelShow.value = {
        column: false,
        filter: false,
        [panel]: !isPanelShow.value[panel],
      }
    }

    /**
     * ---
     * 検索機能の利用
     * ---
     */
    const {
      condition,
      isSomeConditionSet,
      updateCondition,
      getBlankCondition,
    } = useUserListFilterCondition()
    const { isFilterPresent, filterFunction } = useUserListFilterFunction({
      condition,
      getBlankCondition,
      isLoading,
      columnDefs,
      context,
      gridApi,
    })

    /**
     * ---
     * CSVダウンロードの利用
     * ---
     */
    const { download: downloadCsv } = useUserListCsv({
      gridApi,
      columnDefs,
      columnStates,
      updateDate,
      isSomeConditionSet,
    })

    return {
      ROW_HEIGHT,
      handleApiError,
      onGridReady,
      isLoading,
      data,
      updateListData,
      count,
      onRowModelUpdated,
      listUpdatedLabel,
      dataTypeDefinitions,
      defaultColumnDef,
      columnDefs,
      columnDefsWithInitialState,
      columnStates,
      hasSort,
      clearSort,
      columnVisible,
      applyColumnVisible,
      onColumnChanged,
      onFirstDataRendered,
      context,
      isPanelShow,
      togglePanel,
      condition,
      updateCondition,
      getBlankCondition,
      isFilterPresent,
      filterFunction,
      downloadCsv,
    }
  },
  data: function () {
    return {
      // 選択値
      value: [],
      // 検索に関連する全ての状態が空の場合はtrue
      isAllSearchStateEmpty: true,
    }
  },
  computed: {
    /**
     * メッセージの権限がある場合はtrue
     * @type {boolean}
     */
    isMessageAvailable: function () {
      return !!this.$store.getters.functionType(FUNCTION_TYPE.SEND_MESSAGE)
    },
    /**
     * 選択値のカウント
     * @type {number}
     */
    valueCount: function () {
      return this.value.length
    },
    /**
     * メッセージ送信対象ユーザー
     */
    defaultReceiveUsers: function () {
      return this.value.map((u) => ({ id: u.id, role_id: u.role_id }))
    },
  },
  created: function () {
    this.$_updateUserAttributes()
  },
  mounted: function () {
    this.updateListData()
  },
  methods: {
    /**
     * ユーザー属性マスタデータの更新
     */
    $_updateUserAttributes: async function () {
      try {
        await this.$store.dispatch(
          'userAttribute/updateUserAttributesWithThrottle'
        )
      } catch (error) {
        this.handleApiError(error, () =>
          this.$log.warn('failed to fetch userAttributes. use fetched before.')
        )
      }
    },

    /**
     * メッセージ新規送信ダイアログを表示する
     */
    $_showMessagePost: function () {
      this.$refs['message-post-dialog'].showModal()
    },

    /**
     * 選択を解除する
     */
    $_deselect: function () {
      this.value = []
    },
  },
}
</script>

<style lang="scss" scoped>
@use '../assets/scss/parts/container';
@use '../assets/scss/mixin' as mixin;

#the-list {
  padding: 20px 20px 0 20px;
  overflow: hidden;
}

.list-container {
  width: 100%;
  height: 100%;
  padding: 12px 20px 0;
  background-color: #ffffff;
  border: 1px solid #ebebeb;
  border-radius: $border-radius-small;

  ::v-deep .loading-mask {
    display: grid;
    grid-template-rows: max-content 1fr;
  }
}

.header {
  width: 100%;
  padding: 0;
  margin-bottom: 12px;
  color: $text-color;
  display: flex;
  justify-content: space-between;
  align-items: center;

  .left {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    column-gap: 8px;
  }

  .right {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    column-gap: 8px;
  }

  .title {
    font-size: $font-size-large;
    font-weight: $font-weight-bold;
    line-height: 1.7;
    margin: 0;
  }

  .count {
    font-size: $font-size-large;
    font-weight: $font-weight-bold;
    margin: 0;
  }

  .link {
    &.clear-sort {
      // 追加で 8px 開けて 16px 離れるようにする
      margin-left: 8px;
    }
  }

  .label {
    font-size: $font-size-middle;
  }
}

.button {
  @include mixin.button;
  & {
    height: $button-size;
    column-gap: 4px;
    padding: 0 8px 0 6px;
    font-size: $font-size-small;
  }

  &.search {
    position: relative;
    display: flex;
    justify-content: space-between;
    align-items: center;

    // 条件選択して太字化した時に幅を変化させないため
    // レイアウトは太字の非表示要素(.hidden)で行う
    .text {
      &.hidden {
        visibility: hidden;
        font-weight: $font-weight-bold;
      }
      &:not(.hidden) {
        position: absolute;
        right: 8px;
      }
    }

    &.has-condition {
      color: $text-color-link;
      font-weight: $font-weight-bold;
      border-color: $text-color-link;
      .icon {
        fill: $text-color-link;
      }
    }
  }
}

.link {
  font-size: $font-size-middle;
  color: $text-color-link;
  text-decoration: $text-color-link underline;

  &:hover {
    cursor: pointer;
  }
}

.contents {
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;
}

.grid-container {
  width: 100%;
  height: 100%;
  padding-bottom: 12px;
  display: grid;
  grid-template-rows: 1fr max-content;
}

.table {
  color: $text-color;
  margin: 0;
  text-align: left;
}

.footer {
  width: 100%;
  // 表下部-viewport下端 4rem - 上下スペース 12px * 2
  height: calc(4rem - 2 * 12px);
  padding: 0 20px 0 0;
  margin-top: 12px;
  color: $text-color;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  column-gap: 16px;

  .count {
    font-size: $font-size-middle;
    font-weight: $font-weight-bold;
    line-height: 1.7;
    margin: 0;

    &::after {
      content: ':';
    }
  }

  .action {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    column-gap: 8px;
  }

  .deselect {
    font-size: $font-size-middle;
    color: $text-color-link;
    text-decoration: $text-color-link underline;

    &:hover {
      cursor: pointer;
    }
  }
}
</style>
